import { Alert, Button, Card, Descriptions, Empty, Input, Result, Spin, Table, Tag } from "@arco-design/web-react";
import type { ColumnProps } from "@arco-design/web-react/es/Table";

import { useCurrentAuthUser } from "../../auth";
import { useFinanceReceivableListRuntime } from "../runtime";
import {
  FINANCE_RECEIVABLE_OUTSTANDING_REASONS,
  financeReceivableExcludedAgentCollectionReason,
  financeReceivableOrderStatusLabel,
  financeReceivablePendingAdjustmentHint,
  financeReceivablePriceTierGroupLabel,
  financeReceivableProjectSearchScope,
  financeReceivableReferenceQuoteSourceLabel,
  financeReceivableRosterStatusLabel,
  financeReceivableStoppedMemberHint,
  formatFinanceReceivableAmount,
  formatFinanceReceivableQuoteVersion,
  formatFinanceReceivableTeamId,
  hasFinanceReceivableAmount,
  isFinanceReceivableOrderNotFound,
} from "../service";
import type {
  FinanceReceivableOrderLocateItem,
  FinanceReceivablePriceTier,
  FinanceReceivablePriceTierMember,
  FinanceReceivableProjectSearchItem,
  FinanceReceivableReferenceQuote,
  FinanceReceivableSummary,
} from "../types";
import "./financeReceivable.css";

type Props = {
  orderId: string | null;
  onSelectOrder: (orderId: string) => void;
};

type Runtime = ReturnType<typeof useFinanceReceivableListRuntime>;

/**
 * 应收清单页主体：顶部按团编码查团，有定制列表权限的另可按项目关键字搜项目（名单还没定稿的团
 * 还没有团编码，只能这样找）；下方是选中团的团头与应收；只读。
 */
export function FinanceReceivableListFeaturePage({ orderId, onSelectOrder }: Props) {
  const projectSearchScope = financeReceivableProjectSearchScope(useCurrentAuthUser().data);
  const runtime = useFinanceReceivableListRuntime(orderId, onSelectOrder, projectSearchScope);

  return (
    <main className="finance-receivable-page">
      <Card className="finance-receivable-card">
        <div className="finance-receivable-search-bar">
          <Input
            allowClear
            placeholder="输入团编码"
            value={runtime.teamId}
            onChange={runtime.setTeamId}
            onPressEnter={runtime.submitTeamId}
          />
          <Button type="primary" onClick={runtime.submitTeamId}>
            查询
          </Button>
        </div>
        {runtime.locateOpen ? <LocateState locate={runtime.locate} /> : null}
        {projectSearchScope ? (
          <div className="finance-receivable-search-bar finance-receivable-project-bar">
            <Input
              allowClear
              placeholder="按学校、客户或项目名称搜索"
              value={runtime.projectKeyword}
              onChange={runtime.setProjectKeyword}
              onPressEnter={runtime.submitProjectSearch}
            />
            <Button onClick={runtime.submitProjectSearch}>搜索项目</Button>
          </div>
        ) : null}
        {runtime.projectSearchOpen ? (
          <ProjectSearchResults search={runtime.projectSearch} onSelect={runtime.selectProject} />
        ) : null}
        {runtime.projectOrdersOpen ? (
          <ProjectOrders orders={runtime.projectOrders} onOpen={runtime.openProjectOrder} />
        ) : null}
      </Card>
      {orderId ? (
        <ReceivableSummary summary={runtime.summary} />
      ) : (
        <Card className="finance-receivable-card">
          <Empty
            description={
              projectSearchScope
                ? "输入团编码，或按学校、客户、项目名称搜项目，查看一个团的应收。"
                : "输入团编码查询一个团，查看它的应收。"
            }
          />
        </Card>
      )}
    </main>
  );
}

/** 按团编码定位的过程：查到一个订单时运行时已直接打开它，这里只管查询中、查不到和出错。 */
function LocateState({ locate }: { locate: Runtime["locate"] }) {
  if (locate.isPending) return <Spin className="finance-receivable-search-state" tip="正在查询…" />;
  if (locate.isError) {
    return (
      <Alert
        className="finance-receivable-search-state"
        type="error"
        content="查询失败，请稍后重试。"
        action={<Button size="mini" onClick={() => void locate.refetch()}>重新查询</Button>}
      />
    );
  }
  if (locate.data.length === 0) {
    return <Alert className="finance-receivable-search-state" type="warning" content="没有这个团编码" />;
  }
  return null;
}

/** 搜项目结果：点项目名称选中它，再去查它的交接单。 */
function ProjectSearchResults({
  search,
  onSelect,
}: {
  search: Runtime["projectSearch"];
  onSelect: (projectId: string) => void;
}) {
  if (search.isPending) return <Spin className="finance-receivable-search-state" tip="正在搜索…" />;
  if (search.isError) {
    return (
      <Alert
        className="finance-receivable-search-state"
        type="error"
        content="搜索失败，请稍后重试。"
        action={<Button size="mini" onClick={() => void search.refetch()}>重新搜索</Button>}
      />
    );
  }
  const { items, total } = search.data;
  if (items.length === 0) return <Empty className="finance-receivable-search-state" description="没有找到匹配的项目" />;

  const columns: ColumnProps<FinanceReceivableProjectSearchItem>[] = [
    {
      title: "项目名称",
      dataIndex: "projectName",
      render: (_, item) => (
        <Button type="text" className="finance-receivable-link" onClick={() => onSelect(item.projectId)}>
          {item.projectName}
        </Button>
      ),
    },
    { title: "学校", dataIndex: "schoolName", render: (_, item) => item.schoolName ?? "未填" },
    { title: "客户", dataIndex: "customerName", render: (_, item) => item.customerName ?? "未填" },
    { title: "出发日期", dataIndex: "departureDate", width: 120, render: (_, item) => item.departureDate ?? "未填" },
    { title: "交接单", dataIndex: "hasOrder", width: 90, render: (_, item) => (item.hasOrder ? "有" : "没有") },
  ];
  return (
    <div className="finance-receivable-search-state" data-testid="finance-receivable-project-results">
      <Table<FinanceReceivableProjectSearchItem>
        rowKey="projectId"
        columns={columns}
        data={items}
        pagination={false}
        scroll={{ x: 720 }}
      />
      {total > items.length ? (
        <p className="finance-receivable-note">
          共 {total} 个结果，只显示最近创建的 {items.length} 个，请输入更具体的学校、客户或项目名称。
        </p>
      ) : null}
    </div>
  );
}

/** 选中项目的订单：只有一个时运行时已直接打开它；多个时列出来让人选，没有时明说。 */
function ProjectOrders({
  orders,
  onOpen,
}: {
  orders: Runtime["projectOrders"];
  onOpen: (orderId: string) => void;
}) {
  if (orders.isPending) return <Spin className="finance-receivable-search-state" tip="正在查找这个项目的交接单…" />;
  if (orders.isError) {
    return (
      <Alert
        className="finance-receivable-search-state"
        type="error"
        content="查找交接单失败，请稍后重试。"
        action={<Button size="mini" onClick={() => void orders.refetch()}>重新查找</Button>}
      />
    );
  }
  if (orders.data.length === 0) {
    return <Alert className="finance-receivable-search-state" type="warning" content="这个项目还没有交接单" />;
  }
  if (orders.data.length === 1) return null;

  const columns: ColumnProps<FinanceReceivableOrderLocateItem>[] = [
    {
      title: "团编码",
      dataIndex: "teamId",
      render: (_, order) => (
        <Button type="text" className="finance-receivable-link" onClick={() => onOpen(order.orderId)}>
          {formatFinanceReceivableTeamId(order.teamId)}
        </Button>
      ),
    },
    {
      title: "交接单状态",
      dataIndex: "orderStatus",
      width: 140,
      render: (_, order) => (order.orderStatus ? <Tag>{financeReceivableOrderStatusLabel(order.orderStatus)}</Tag> : null),
    },
  ];
  return (
    <div className="finance-receivable-search-state" data-testid="finance-receivable-project-orders">
      <p className="finance-receivable-hint">这个项目有 {orders.data.length} 个交接单，选一个查看应收。</p>
      <Table<FinanceReceivableOrderLocateItem> rowKey="orderId" columns={columns} data={orders.data} pagination={false} />
    </div>
  );
}

/** 选中团的团头与应收。 */
function ReceivableSummary({ summary }: { summary: Runtime["summary"] }) {
  if (summary.isPending) {
    return (
      <Card className="finance-receivable-card">
        <Spin tip="正在加载应收…" />
      </Card>
    );
  }
  if (summary.isError) {
    return (
      <Card className="finance-receivable-card">
        {isFinanceReceivableOrderNotFound(summary.error) ? (
          <Empty description="没有找到这个订单，请按团编码重新查询。" />
        ) : (
          <Result
            status="error"
            title="应收汇总加载失败，请稍后重试。"
            extra={<Button type="primary" onClick={() => void summary.refetch()}>重试</Button>}
          />
        )}
      </Card>
    );
  }
  const { header, receivable, rosterFinalized, priceTiers, agentCollection, referenceQuote, received, outstanding } =
    summary.data;
  const pendingAdjustmentHint = rosterFinalized
    ? null
    : financeReceivablePendingAdjustmentHint(receivable.pendingFeeAdjustmentTotal);
  return (
    <>
      <Card className="finance-receivable-card" title={header.routeName ?? "未填线路名称"}>
        <Descriptions
          column={3}
          data={[
            { label: "团编码", value: formatFinanceReceivableTeamId(header.teamId) },
            { label: "交接单状态", value: <Tag>{financeReceivableOrderStatusLabel(header.orderStatus)}</Tag> },
            { label: "名单状态", value: <Tag>{financeReceivableRosterStatusLabel(header.rosterStatus)}</Tag> },
          ]}
        />
      </Card>
      <Card className="finance-receivable-card" title="应收">
        {rosterFinalized ? null : (
          <Alert className="finance-receivable-alert" type="info" content="名单还没定稿，应收记 0。" />
        )}
        {pendingAdjustmentHint ? (
          <Alert className="finance-receivable-alert" type="warning" content={pendingAdjustmentHint} />
        ) : null}
        <div data-testid="finance-receivable-totals">
          <Descriptions
            column={3}
            layout="vertical"
            data={[
              { label: "冻结基准应收合计", value: <Money value={receivable.frozenBaseReceivableTotal} /> },
              { label: "费用调整合计", value: <Money value={receivable.feeAdjustmentTotal} /> },
              {
                label: "当前个人应收合计",
                value: <strong><Money value={receivable.currentReceivableTotal} /></strong>,
              },
            ]}
          />
        </div>
        <p className="finance-receivable-note">
          已收只算通过本系统团专属二维码、银行卡收到的钱；团款目前大多还在旧系统收，这里的已收会偏少、尚欠会偏多。
        </p>
        <p className="finance-receivable-note">
          收付款验证页的「应收」「未收」用的是订单上的合同总额，不含代收，所以两边会不一样。
        </p>
        {rosterFinalized ? <RosterComparison receivable={receivable} /> : null}
      </Card>
      <ReceivedAndOutstanding received={received} outstanding={outstanding} />
      {referenceQuote ? <ReferenceQuote referenceQuote={referenceQuote} /> : null}
      {rosterFinalized ? <PriceTiers tiers={priceTiers} /> : null}
      <AgentCollection agentCollection={agentCollection} rosterFinalized={rosterFinalized} />
    </>
  );
}

/**
 * 名单定稿后，名单之外的几个数：订单头合同总额照列、对不上时多一行差额；
 * 停用成员与已不在有效名单上的成员名下的调整都不计入，有才提示，不悄悄丢掉。
 */
function RosterComparison({ receivable }: { receivable: FinanceReceivableSummary["receivable"] }) {
  const stoppedHint = financeReceivableStoppedMemberHint(receivable.stoppedMemberCount);
  const hasExcludedAdjustment = hasFinanceReceivableAmount(receivable.excludedFeeAdjustmentTotal);
  return (
    <div className="finance-receivable-comparison">
      <Descriptions
        column={1}
        data={[
          { label: "订单头合同总额", value: <Money value={receivable.orderContractTotal} /> },
          ...(hasFinanceReceivableAmount(receivable.orderContractGap)
            ? [{ label: "订单头与名单差额", value: <Money value={receivable.orderContractGap} /> }]
            : []),
          ...(hasExcludedAdjustment
            ? [{ label: "未计入的调整合计", value: <Money value={receivable.excludedFeeAdjustmentTotal} /> }]
            : []),
        ]}
      />
      {stoppedHint ? <Alert className="finance-receivable-alert" type="warning" content={stoppedHint} /> : null}
      <p className="finance-receivable-note">
        应收以名单为准。订单头合同总额是合同上印的数，只照列；差额＝订单头合同总额 − 冻结基准应收合计。
        {hasExcludedAdjustment ? "未计入的调整是已不在有效名单上的成员名下的费用调整。" : null}
      </p>
    </div>
  );
}

/**
 * 名单定稿前的参考报价：已采用成交报价的列成交报价，没采用的列这个行程最新一版定稿报价；
 * 只作参考，不计入任何合计。读不出来时只说明这一块，页面其他部分照常。
 */
function ReferenceQuote({ referenceQuote }: { referenceQuote: FinanceReceivableReferenceQuote }) {
  return (
    <Card
      className="finance-receivable-card"
      data-testid="finance-receivable-reference-quote"
      title={
        <span>
          参考报价 <Tag color="gray">参考</Tag>
        </span>
      }
    >
      {referenceQuote.status === "none" ? <Empty description="暂无定稿报价" /> : null}
      {referenceQuote.status === "unreadable" ? <Alert type="warning" content="参考报价暂时读不出来" /> : null}
      {referenceQuote.status === "available" && referenceQuote.source ? (
        <Descriptions
          column={2}
          layout="vertical"
          data={[
            { label: "来源", value: financeReceivableReferenceQuoteSourceLabel(referenceQuote.source) },
            {
              label: "版本",
              value: formatFinanceReceivableQuoteVersion(referenceQuote.versionNo, referenceQuote.versionName),
            },
            { label: "合同应收", value: <Money value={referenceQuote.contractReceivableAmount} /> },
            { label: "代收代付", value: <Money value={referenceQuote.agentCollectionAmount} /> },
          ]}
        />
      ) : null}
      <p className="finance-receivable-note">
        名单还没定稿，这里只作参考，不计入任何合计。合同应收是报价上取整前的签约额，不含代收代付。
      </p>
    </Card>
  );
}

/** 价格档的行键：同一分组里单价不同才分档。 */
function priceTierKey(tier: FinanceReceivablePriceTier): string {
  return `${tier.group}-${tier.unitPrice}`;
}

/**
 * 价格档：分组照合同交接表「实际出团」行，组内按冻结基准应收分档，顺序沿用接口给的。
 * 签约后的费用调整在档内逐人列、不拆档。点一档原地展开这一档的人，再点收起，可以同时展开多档。
 */
function PriceTiers({ tiers }: { tiers: FinanceReceivablePriceTier[] }) {
  const columns: ColumnProps<FinanceReceivablePriceTier>[] = [
    { title: "分组", dataIndex: "group", render: (_, tier) => financeReceivablePriceTierGroupLabel(tier.group) },
    {
      title: "单价",
      dataIndex: "unitPrice",
      width: 140,
      align: "right",
      render: (_, tier) => <Money value={tier.unitPrice} />,
    },
    { title: "人数", dataIndex: "memberCount", width: 90, align: "right", render: (_, tier) => `${tier.memberCount} 人` },
    {
      title: "基准小计",
      dataIndex: "frozenBaseReceivableSubtotal",
      width: 150,
      align: "right",
      render: (_, tier) => <Money value={tier.frozenBaseReceivableSubtotal} />,
    },
    {
      title: "调整小计",
      dataIndex: "feeAdjustmentSubtotal",
      width: 150,
      align: "right",
      render: (_, tier) => <Money value={tier.feeAdjustmentSubtotal} />,
    },
    {
      title: "当前小计",
      dataIndex: "currentReceivableSubtotal",
      width: 150,
      align: "right",
      render: (_, tier) => <Money value={tier.currentReceivableSubtotal} />,
    },
  ];
  return (
    <Card className="finance-receivable-card" title="价格档">
      <div data-testid="finance-receivable-price-tiers">
        <Table<FinanceReceivablePriceTier>
          rowKey={priceTierKey}
          columns={columns}
          data={tiers}
          pagination={false}
          scroll={{ x: 800 }}
          noDataElement={<Empty description="名单上没有有效成员" />}
          expandedRowRender={(tier) => <PriceTierMembers tier={tier} />}
          expandProps={{ expandRowByClick: true }}
        />
      </div>
      <p className="finance-receivable-note">
        按冻结基准应收分档；签约后的费用调整在档内逐人列，不拆档。各档小计加起来等于上面三个合计。
      </p>
    </Card>
  );
}

/**
 * 已收与尚欠：已收合计与团专属二维码、银行卡两个渠道小计，一张收款单都没开过时写「还没有收款记录」，不写 0；
 * 尚欠写金额或算不出的原因，多收时另起一行写超收待对账。
 */
function ReceivedAndOutstanding({
  received,
  outstanding,
}: {
  received: FinanceReceivableSummary["received"];
  outstanding: FinanceReceivableSummary["outstanding"];
}) {
  const overpaid = outstanding.kind === "computed" && hasFinanceReceivableAmount(outstanding.overpaidAmount);
  return (
    <Card className="finance-receivable-card" title="已收与尚欠">
      <div data-testid="finance-receivable-received">
        {received.hasCollection ? (
          <Descriptions
            column={3}
            layout="vertical"
            data={[
              { label: "已收合计", value: <strong><Money value={received.totalAmount} /></strong> },
              { label: "团专属二维码", value: <Money value={received.qrCodeAmount} /> },
              { label: "银行卡", value: <Money value={received.bankCardAmount} /> },
            ]}
          />
        ) : (
          <p className="finance-receivable-hint">还没有收款记录</p>
        )}
      </div>
      <div data-testid="finance-receivable-outstanding">
        <Descriptions
          column={3}
          layout="vertical"
          data={[
            {
              label: "尚欠",
              value:
                outstanding.kind === "computed" ? (
                  <strong><Money value={outstanding.amount} /></strong>
                ) : (
                  FINANCE_RECEIVABLE_OUTSTANDING_REASONS[outstanding.kind]
                ),
            },
          ]}
        />
        {overpaid ? (
          <Alert
            className="finance-receivable-alert"
            type="warning"
            content={`超收待对账 ${formatFinanceReceivableAmount(outstanding.overpaidAmount)}，不会自动退款`}
          />
        ) : null}
      </div>
    </Card>
  );
}

/** 代收代付：订单头四类及成交报价门票金额单列，不进应收；客户支付总额＝当前个人应收合计＋代收代付合计。 */
function AgentCollection({
  agentCollection,
  rosterFinalized,
}: {
  agentCollection: FinanceReceivableSummary["agentCollection"];
  rosterFinalized: boolean;
}) {
  return (
    <Card className="finance-receivable-card" title="代收代付">
      <div data-testid="finance-receivable-agent-collection">
        {rosterFinalized ? null : (
          <Alert
            className="finance-receivable-alert"
            type="info"
            content="名单还没定稿，代收代付和客户支付总额记 0。"
          />
        )}
        <Descriptions
          column={4}
          layout="vertical"
          tableLayout="fixed"
          data={[
            { label: "火车票", value: <Money value={agentCollection.trainTicketAmount} /> },
            { label: "机票", value: <Money value={agentCollection.flightTicketAmount} /> },
            { label: "非遗", value: <Money value={agentCollection.intangibleAmount} /> },
            { label: "校企", value: <Money value={agentCollection.schoolEnterpriseAmount} /> },
            { label: "门票与考察", value: <Money value={agentCollection.ticketAmount} /> },
          ]}
        />
        <div className="finance-receivable-agent-totals">
          <Descriptions
            column={2}
            layout="vertical"
            data={[
              { label: "代收代付合计", value: <Money value={agentCollection.agentCollectionTotal} /> },
              {
                label: "客户支付总额",
                value: <strong><Money value={agentCollection.customerPaymentTotal} /></strong>,
              },
            ]}
          />
        </div>
        {agentCollection.excludedItems.length > 0 ? (
          <Alert
            className="finance-receivable-excluded"
            type="warning"
            title="未计入的代收"
            content={
              <ul className="finance-receivable-excluded-list">
                {agentCollection.excludedItems.map((item) => (
                  <li key={item.category}>{financeReceivableExcludedAgentCollectionReason(item)}</li>
                ))}
              </ul>
            }
          />
        ) : null}
      </div>
      <p className="finance-receivable-note">
        代收代付取交接单上填的金额，不进应收，也不进应付；「是否代收代付大交通」选否时火车票、机票按 0，「是否代收代付非遗校企」选否时非遗、校企按 0。客户支付总额＝当前个人应收合计＋代收代付合计。
      </p>
    </Card>
  );
}

/** 一档展开后的成员，照名单顺序。 */
function PriceTierMembers({ tier }: { tier: FinanceReceivablePriceTier }) {
  const columns: ColumnProps<FinanceReceivablePriceTierMember>[] = [
    { title: "姓名", dataIndex: "memberName" },
    {
      title: "冻结基准应收",
      dataIndex: "frozenBaseReceivableAmount",
      width: 150,
      align: "right",
      render: (_, member) => <Money value={member.frozenBaseReceivableAmount} />,
    },
    {
      title: "费用调整",
      dataIndex: "feeAdjustmentAmount",
      width: 150,
      align: "right",
      render: (_, member) => <Money value={member.feeAdjustmentAmount} />,
    },
    {
      title: "当前个人应收",
      dataIndex: "currentReceivableAmount",
      width: 150,
      align: "right",
      render: (_, member) => <Money value={member.currentReceivableAmount} />,
    },
  ];
  return (
    <div data-testid={`finance-receivable-tier-members-${priceTierKey(tier)}`}>
      <Table<FinanceReceivablePriceTierMember>
        rowKey="memberId"
        size="small"
        columns={columns}
        data={tier.members}
        pagination={false}
      />
    </div>
  );
}

/** 金额：¥ 加千分位两位小数，数字等宽。 */
function Money({ value }: { value: string | null }) {
  return <span className="finance-receivable-money">{formatFinanceReceivableAmount(value)}</span>;
}
